import { gcm } from '@noble/ciphers/aes.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';

import { bytesToBase64, decodeFlexibleInput } from './paymentCrypto';

export type AccountPayUnlockPayload = {
  payPwdPlain: string;
  payPwdWire: string;
};

const HKDF_INFO = new TextEncoder().encode('cointrader:account-pay-unlock:v1');

function derivePayUnlockKey(sessionToken: string): Uint8Array {
  const ikm = new TextEncoder().encode(sessionToken.trim());
  if (!ikm.length) {
    throw new Error('SESSION_TOKEN_EMPTY');
  }
  return hkdf(sha256, ikm, undefined, HKDF_INFO, 32);
}

export async function encryptAccountPayUnlock(
  sessionToken: string,
  payload: AccountPayUnlockPayload,
): Promise<string> {
  if (typeof globalThis.crypto?.getRandomValues !== 'function') {
    throw new Error('CRYPTO_RANDOM_UNAVAILABLE');
  }
  const keyBytes = derivePayUnlockKey(sessionToken);
  const iv = new Uint8Array(12);
  globalThis.crypto.getRandomValues(iv);
  const plain = new TextEncoder().encode(JSON.stringify(payload));
  const aes = gcm(keyBytes, iv);
  const ct = aes.encrypt(plain);
  const out = new Uint8Array(iv.length + ct.length);
  out.set(iv, 0);
  out.set(ct, iv.length);
  return bytesToBase64(out);
}

export async function decryptAccountPayUnlock(
  sessionToken: string,
  ciphertextB64: string,
): Promise<AccountPayUnlockPayload | null> {
  try {
    const all = decodeFlexibleInput(ciphertextB64);
    if (all.length < 12 + 16) {
      return null;
    }
    const iv = all.slice(0, 12);
    const ct = all.slice(12);
    const keyBytes = derivePayUnlockKey(sessionToken);
    const aes = gcm(keyBytes, iv);
    const plain = aes.decrypt(ct);
    const parsed = JSON.parse(new TextDecoder().decode(plain)) as {
      payPwdPlain?: string;
      payPwdWire?: string;
    };
    const payPwdPlain = parsed.payPwdPlain?.trim() ?? '';
    const payPwdWire = parsed.payPwdWire?.trim() ?? '';
    if (!payPwdPlain || !payPwdWire) {
      return null;
    }
    return { payPwdPlain, payPwdWire };
  } catch {
    return null;
  }
}
